import { SubscriptionPlan } from "./types";
import { formatPrice, getBadgeLabel, badgeOptions } from "./utils";

interface SubscriptionPlanCardProps {
  plan: SubscriptionPlan;
  onEdit: (plan: SubscriptionPlan) => void;
  onIssue: (plan: SubscriptionPlan) => void;
}

export const SubscriptionPlanCard = ({
  plan,
  onEdit,
  onIssue,
}: SubscriptionPlanCardProps) => {
  // 고정 유효기간이 이미 지난 이용권 — "활성"으로 설정돼 있어도 발급 불가.
  // 사장님이 만료 상태를 한눈에 알 수 있도록 뱃지로 표시.
  const isExpired =
    plan.durationDays == null &&
    !!plan.dtExpired &&
    new Date(plan.dtExpired as string).getTime() <= Date.now();

  return (
    <div className="bg-white border-2 border-[#E9E9E9] rounded-xl p-4">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex flex-wrap gap-2 items-center mb-1">
            <h3 className="text-lg font-semibold">{plan.planName}</h3>
            {plan.activeYn === "Y" ? (
              <span className="px-2 py-1 text-xs font-medium text-green-600 bg-green-100 rounded-full">
                활성
              </span>
            ) : (
              <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-[#F5F5F5] rounded-full">
                비활성
              </span>
            )}
            {isExpired && (
              <span className="px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-full">
                만료
              </span>
            )}
            {/* 뱃지 표시 */}
            {plan.badges.map((badge) => (
              <span
                key={badge}
                className="px-2 py-1 text-xs font-medium text-orange-600 bg-orange-100 rounded-full"
              >
                {getBadgeLabel(badge, badgeOptions)}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-600">{plan.content}</p>
        </div>
      </div>

      <div className="flex flex-col gap-2 mb-4">
        {plan.discountRate > 0 ? (
          <>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">가격</span>
              <span className="text-sm text-gray-500 line-through">
                {formatPrice(plan.price)}원
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">할인율</span>
              <span className="text-sm font-medium text-red-500">
                {plan.discountRate}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">할인 금액</span>
              <span className="text-base font-semibold text-orange-500">
                {formatPrice(plan.discountedPrice)}원
              </span>
            </div>
          </>
        ) : (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">가격</span>
            <span className="text-base font-semibold text-orange-500">
              {formatPrice(plan.price)}원
            </span>
          </div>
        )}
      </div>

      {/* 관리 버튼 */}
      <div className="flex gap-2 pt-4 border-t border-gray-200">
        <button
          onClick={() => onEdit(plan)}
          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-lg transition-colors hover:bg-orange-500"
        >
          수정
        </button>
        <button
          onClick={() => onIssue(plan)}
          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-lg transition-colors hover:bg-orange-500"
        >
          이용권 발급
        </button>
      </div>
    </div>
  );
};
